"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import {
  brandAssetApi,
  type BrandAssetLogoItem,
  type BrandAssetLogoLibrary,
  type LogoPresetItem,
  type LogoSizeMode,
} from "../../lib/brand-asset-api";
import { EcomSelect, EcomSelectOption, useEcomFeedback } from "../../ui";
import { useEcomPortal } from "../../ui/portal";

export interface LogoPresetPayload {
  enabled: boolean;
  items: LogoPresetItem[];
}

interface LogoPresetPanelProps {
  moduleCode: string;
  value: LogoPresetPayload;
  onChange: (value: LogoPresetPayload) => void;
  /** 工作流中已上传的底图 URL，用于 Logo 选择弹窗右侧预览 */
  sourceImageUrl?: string | null;
  /** 工作流输出裁剪尺寸，如 1440x1920 */
  outputCropSize?: string | null;
  /** 工作流比例提示，如 16:9 */
  aspectRatioHint?: string | null;
}

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };

function parseDefaultConfig(item: BrandAssetLogoItem): Partial<LogoPresetItem> {
  if (!item.default_config) return {};
  try {
    return JSON.parse(item.default_config) as Partial<LogoPresetItem>;
  } catch {
    return {};
  }
}

const FALLBACK_INITIAL_POSITION = "top_left";

type LogoNaturalSize = { width: number; height: number };

/**
 * 读取 Logo 资源的宽高比。
 * - 优先使用后端提供的 width/height；
 * - 兜底使用前端运行时从 <img> 测量到的自然尺寸（DB 字段缺失时仍可推荐）。
 */
function getLogoAspectRatio(
  item: BrandAssetLogoItem,
  fallback?: LogoNaturalSize | null,
): number | null {
  if (item.width && item.height && item.height > 0) {
    return item.width / item.height;
  }
  if (fallback && fallback.width > 0 && fallback.height > 0) {
    return fallback.width / fallback.height;
  }
  return null;
}

/**
 * 判断 Logo 是否与底图比例相近。
 * - 相对差 <= 8% 直接命中；
 * - 同为竖版或横版且比例相近也命中，避免用户上传手机竖图时 3:4 / 2:3 / 9:16 之间完全不推荐；
 * - 对极端宽 / 极端高比例放宽少量绝对误差。
 */
function isLogoRecommendedForSource(
  item: BrandAssetLogoItem,
  sourceAspectRatio: number | null | undefined,
  fallbackSize?: LogoNaturalSize | null,
): boolean {
  if (
    sourceAspectRatio === null ||
    sourceAspectRatio === undefined ||
    !Number.isFinite(sourceAspectRatio) ||
    sourceAspectRatio <= 0
  ) {
    return false;
  }
  const logoAspect = getLogoAspectRatio(item, fallbackSize);
  if (!logoAspect || logoAspect <= 0) return false;
  const max = Math.max(logoAspect, sourceAspectRatio);
  const min = Math.min(logoAspect, sourceAspectRatio);
  if ((max - min) / max <= 0.08) return true;
  const samePortraitFamily = logoAspect < 1 && sourceAspectRatio < 1;
  const sameLandscapeFamily = logoAspect > 1 && sourceAspectRatio > 1;
  if ((samePortraitFamily || sameLandscapeFamily) && min / max >= 0.72) return true;
  if (min >= 3 && max - min <= 1.0) return true;
  if (max <= 0.4 && max - min <= 0.1) return true;
  return false;
}

/**
 * 根据 Logo 宽高比给出同步 fallback 方位：
 * - 4:1 及以上的宽带状素材默认 `bottom_center`；
 * - 极端瘦高素材默认 `center`；
 * - 其他情况默认 `top_left`。
 */
function inferDefaultPositionFromAspect(
  item: BrandAssetLogoItem,
  fallback?: LogoNaturalSize | null,
): string {
  const ratio = getLogoAspectRatio(item, fallback);
  if (ratio && ratio >= 4) return "bottom_center";
  if (ratio && ratio <= 0.25) return "center";
  return FALLBACK_INITIAL_POSITION;
}

/**
 * 选择 Logo 加入工作流时的初始方位：
 * 1) 优先使用透明像素分析得到的位置（用户要求智能识别优先）；
 * 2) 次优使用后台 `default_config.position`，但忽略与历史硬编码相同的 `top_center`，
 *    避免一旦后台空配置或保留旧默认就回到固定值；
 * 3) 兜底按宽高比推断。
 */
function pickInitialPosition(
  item: BrandAssetLogoItem,
  defaults: Partial<LogoPresetItem>,
  analyzedPosition?: string,
  fallback?: LogoNaturalSize | null,
): string {
  if (analyzedPosition) return analyzedPosition;
  const configured = typeof defaults.position === "string" ? defaults.position.trim() : "";
  // 跳过历史固定值 "top_center"，让智能 fallback 接手；其他显式配置仍尊重。
  if (configured !== "" && configured !== "top_center") {
    return configured;
  }
  return inferDefaultPositionFromAspect(item, fallback);
}

const ALLOWED_AUTO_POSITIONS = new Set<string>([
  "top_left",
  "top_center",
  "top_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
  "center",
]);

/**
 * 通过 canvas 读取 Logo 不透明像素的分布，估计其内容重心，并映射到允许的方位。
 * 任意失败（跨域 tainted、不可解码等）均静默返回 null，调用方继续使用 fallback。
 */
async function analyzeLogoContentPosition(url: string): Promise<string | null> {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      const timer = window.setTimeout(() => finish(null), 5000);
      img.onload = () => {
        window.clearTimeout(timer);
        try {
          const nw = img.naturalWidth;
          const nh = img.naturalHeight;
          if (!nw || !nh) {
            finish(null);
            return;
          }
          const maxDim = 192;
          const scale = Math.min(1, maxDim / Math.max(nw, nh));
          const cw = Math.max(1, Math.round(nw * scale));
          const ch = Math.max(1, Math.round(nh * scale));
          const canvas = document.createElement("canvas");
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) {
            finish(null);
            return;
          }
          ctx.clearRect(0, 0, cw, ch);
          ctx.drawImage(img, 0, 0, cw, ch);
          let data: Uint8ClampedArray;
          try {
            data = ctx.getImageData(0, 0, cw, ch).data;
          } catch {
            finish(null);
            return;
          }
          let minX = cw;
          let minY = ch;
          let maxX = -1;
          let maxY = -1;
          let opaqueCount = 0;
          const totalCount = cw * ch;
          const alphaThreshold = 16;
          for (let y = 0; y < ch; y++) {
            for (let x = 0; x < cw; x++) {
              const idx = (y * cw + x) * 4 + 3;
              if (data[idx] > alphaThreshold) {
                opaqueCount++;
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0 || maxY < 0) {
            finish(null);
            return;
          }
          // 几乎完全不透明的素材（如 jpg、白底 png）无法判定方位，留给宽高比兜底
          if (totalCount > 0 && opaqueCount / totalCount > 0.95) {
            finish(null);
            return;
          }
          const cx = ((minX + maxX) / 2) / cw;
          const cy = ((minY + maxY) / 2) / ch;
          const bboxW = (maxX - minX + 1) / cw;

          let xBand: "left" | "center" | "right";
          if (bboxW > 0.8) xBand = "center";
          else if (cx < 0.34) xBand = "left";
          else if (cx > 0.66) xBand = "right";
          else xBand = "center";

          let yBand: "top" | "middle" | "bottom";
          if (cy < 0.34) yBand = "top";
          else if (cy > 0.66) yBand = "bottom";
          else yBand = "middle";

          if (yBand === "middle" && xBand === "center") {
            finish("center");
            return;
          }
          if (yBand === "middle") {
            // 中间偏左/右更接近常见品牌摆放，归到顶部对应方位
            finish(xBand === "left" ? "top_left" : "top_right");
            return;
          }
          // 任何底部内容（包括偏左/偏右）统一落到正下，符合用户对"透明在底部素材→正下"的诉求。
          if (yBand === "bottom") {
            finish("bottom_center");
            return;
          }
          // yBand === "top"：保留 top_left / top_center / top_right。
          const key = `top_${xBand}`;
          finish(ALLOWED_AUTO_POSITIONS.has(key) ? key : null);
        } catch {
          finish(null);
        }
      };
      img.onerror = () => {
        window.clearTimeout(timer);
        finish(null);
      };
      img.src = url;
    } catch {
      finish(null);
    }
  });
}

function toPresetItem(
  library: BrandAssetLogoLibrary,
  item: BrandAssetLogoItem,
  index: number,
  initialPosition?: string,
  fallbackSize?: LogoNaturalSize | null,
): LogoPresetItem {
  const defaults = parseDefaultConfig(item);
  const sizeMode: LogoSizeMode = defaults.size_mode === "ratio" ? "ratio" : "original";
  const targetShortSide = resolveLogoShortSide(
    item.width || fallbackSize?.width,
    item.height || fallbackSize?.height,
  );
  return {
    library_id: library.id,
    asset_id: item.id,
    position: initialPosition || pickInitialPosition(item, defaults),
    width_ratio: 0,
    size_mode: sizeMode,
    ...(targetShortSide ? { target_short_side: targetShortSide } : {}),
    opacity: 1,
    z_index: typeof defaults.z_index === "number" ? defaults.z_index : (index + 1) * 10,
    margin: defaultMargin,
  };
}

const POSITION_LABELS: Record<string, string> = {
  top_left: "左上",
  top_center: "正上",
  top_right: "右上",
  bottom_left: "左下",
  bottom_center: "正下",
  bottom_right: "右下",
  center: "居中",
};

// Fallback short side used when a logo's real dimensions are unavailable.
// Known logo assets drive the preview/output canvas by their own short side.
const PREVIEW_FALLBACK_SHORT_SIDE = 1440;

const PREVIEW_ASSET_SIZES: Record<number, number> = {
  // Fallback for legacy logo assets whose real size has not been measured yet.
  1440: 100,
};

const LOGO_PREVIEW_BG = "#dbeafe";

function formatLogoDimension(item: BrandAssetLogoItem) {
  if (item.width && item.height) {
    return `${item.width}×${item.height}`;
  }
  return null;
}

function LogoGridPreview({
  item,
  onNaturalSize,
}: {
  item: BrandAssetLogoItem;
  /** 当 DB 缺少 width/height 时，把图片真实尺寸抛回给上层用于"推荐"匹配。 */
  onNaturalSize?: (assetId: number, width: number, height: number) => void;
}) {
  const initialRatio = item.width && item.height ? item.width / item.height : null;
  const [aspectRatio, setAspectRatio] = useState<number | null>(initialRatio);

  return (
    <div
      className="mb-1.5 flex w-full items-center justify-center overflow-hidden rounded"
      style={{
        backgroundColor: LOGO_PREVIEW_BG,
        aspectRatio: aspectRatio ? `${aspectRatio}` : "1",
        maxHeight: 240,
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={item.thumbnail_url || item.file_url}
        alt={item.name}
        draggable={false}
        className="max-h-full max-w-full select-none object-contain"
        onLoad={(event) => {
          const img = event.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setAspectRatio(img.naturalWidth / img.naturalHeight);
            onNaturalSize?.(item.id, img.naturalWidth, img.naturalHeight);
          }
        }}
      />
    </div>
  );
}

function isOriginalMode(item: LogoPresetItem): boolean {
  return (item.size_mode ?? "ratio") === "original";
}

function getPresetTargetShortSide(item: LogoPresetItem): number | null {
  const value = item.target_short_side;
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : null;
}

function getLogoOverlayStyle(
  item: LogoPresetItem,
  logoWidthPercent?: number,
  canvasReferenceWidth: number = PREVIEW_FALLBACK_SHORT_SIDE,
): CSSProperties {
  const original = isOriginalMode(item);
  const mt = `${(item.margin.top / canvasReferenceWidth) * 100}%`;
  const mr = `${(item.margin.right / canvasReferenceWidth) * 100}%`;
  const mb = `${(item.margin.bottom / canvasReferenceWidth) * 100}%`;
  const ml = `${(item.margin.left / canvasReferenceWidth) * 100}%`;
  const base: CSSProperties = original
    ? {
        position: "absolute",
        width: `${logoWidthPercent ?? PREVIEW_ASSET_SIZES[1440]}%`,
        maxWidth: "100%",
        opacity: item.opacity,
        zIndex: item.z_index,
        pointerEvents: "none",
      }
    : {
        position: "absolute",
        width: `${Math.max(4, Math.min(100, item.width_ratio * 100))}%`,
        opacity: item.opacity,
        zIndex: item.z_index,
        pointerEvents: "none",
      };
  switch (item.position) {
    case "top_left":
      return { ...base, top: mt, left: ml };
    case "top_center":
      return { ...base, top: mt, left: "50%", transform: "translateX(-50%)" };
    case "top_right":
      return { ...base, top: mt, right: mr };
    case "bottom_left":
      return { ...base, bottom: mb, left: ml };
    case "bottom_center":
      return { ...base, bottom: mb, left: "50%", transform: "translateX(-50%)" };
    case "center":
      return { ...base, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    case "bottom_right":
    default:
      return { ...base, bottom: mb, right: mr };
  }
}

function parseOutputCropSize(value: string | null | undefined): { width: number; height: number } | null {
  if (!value?.trim()) return null;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "").replace("*", "x");
  const match = normalized.match(/^(\d+)x(\d+)$/);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!width || !height) return null;
  return { width, height };
}

function parseAspectRatioHint(value: string | null | undefined): number | null {
  if (!value?.trim() || value.trim() === "auto") return null;
  const match = value.trim().match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const w = Number(match[1]);
  const h = Number(match[2]);
  if (!w || !h) return null;
  return w / h;
}

type PreviewFitMode = "native" | "cover-bottom";

function resolveLogoShortSide(width: number | null | undefined, height: number | null | undefined): number | null {
  if (!width || !height || width <= 0 || height <= 0) return null;
  return Math.max(1, Math.round(Math.min(width, height)));
}

function normalizeCanvasSizeToShortSideValue(
  width: number,
  height: number,
  shortSide: number,
): { width: number; height: number } {
  const safeShortSide = shortSide > 0 ? shortSide : PREVIEW_FALLBACK_SHORT_SIDE;
  if (width <= 0 || height <= 0) {
    return { width: safeShortSide, height: safeShortSide };
  }
  if (width <= height) {
    return {
      width: safeShortSide,
      height: Math.max(1, Math.round((height / width) * safeShortSide)),
    };
  }
  return {
    width: Math.max(1, Math.round((width / height) * safeShortSide)),
    height: safeShortSide,
  };
}

function computeSourcePreviewLayout(
  naturalWidth: number,
  naturalHeight: number,
  logoReferenceWidth: number,
  logoReferenceHeight: number,
  logoReferenceShortSide: number,
  outputCropSize: string | null | undefined,
  aspectRatioHint: string | null | undefined,
): { aspectRatio: number; canvasReferenceWidth: number; fitMode: PreviewFitMode } {
  const crop = parseOutputCropSize(outputCropSize);
  const hintAspect = parseAspectRatioHint(aspectRatioHint);
  const targetShortSide = logoReferenceShortSide > 0 ? logoReferenceShortSide : PREVIEW_FALLBACK_SHORT_SIDE;

  if (crop) {
    const normalizedCrop = normalizeCanvasSizeToShortSideValue(crop.width, crop.height, targetShortSide);
    return {
      aspectRatio: crop.width / crop.height,
      canvasReferenceWidth: normalizedCrop.width,
      fitMode: "cover-bottom",
    };
  }

  if (!naturalWidth || !naturalHeight) {
    const fallbackAspect = hintAspect && hintAspect > 0
      ? hintAspect
      : logoReferenceHeight > 0 && logoReferenceWidth > 0
        ? logoReferenceWidth / logoReferenceHeight
        : 1;
    const fallbackSize = normalizeCanvasSizeToShortSideValue(fallbackAspect, 1, targetShortSide);
    return {
      aspectRatio: fallbackSize.width / fallbackSize.height,
      canvasReferenceWidth: fallbackSize.width,
      fitMode: "native",
    };
  }

  const normalizedSize = normalizeCanvasSizeToShortSideValue(naturalWidth, naturalHeight, targetShortSide);
  return {
    aspectRatio: normalizedSize.width / normalizedSize.height,
    canvasReferenceWidth: normalizedSize.width,
    fitMode: "native",
  };
}

function PreviewCanvas({
  selectedItems,
  resolveThumb,
  resolveName,
  resolveLogoWidth,
  resolveLogoHeight,
  sourceImageUrl,
  outputCropSize,
  aspectRatioHint,
}: {
  selectedItems: LogoPresetItem[];
  resolveThumb: (assetId: number) => string | null;
  resolveName: (assetId: number) => string;
  resolveLogoWidth?: (assetId: number) => number | null;
  resolveLogoHeight?: (assetId: number) => number | null;
  sourceImageUrl?: string | null;
  outputCropSize?: string | null;
  aspectRatioHint?: string | null;
}) {
  const [logoNaturalSizes, setLogoNaturalSizes] = useState<Record<number, LogoNaturalSize>>({});
  const [sourceNaturalSize, setSourceNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const logoReferenceWidth = selectedItems.reduce((max, item) => {
    const width = resolveLogoWidth?.(item.asset_id) || logoNaturalSizes[item.asset_id]?.width;
    return width && width > max ? width : max;
  }, PREVIEW_FALLBACK_SHORT_SIDE);

  const logoReferenceHeight = selectedItems.reduce((max, item) => {
    const height = resolveLogoHeight?.(item.asset_id) || logoNaturalSizes[item.asset_id]?.height;
    return height && height > max ? height : max;
  }, 0);

  const logoReferenceShortSide = selectedItems.reduce((min, item) => {
    const shortSide =
      getPresetTargetShortSide(item) ||
      resolveLogoShortSide(
        resolveLogoWidth?.(item.asset_id) || logoNaturalSizes[item.asset_id]?.width,
        resolveLogoHeight?.(item.asset_id) || logoNaturalSizes[item.asset_id]?.height,
      );
    if (!shortSide) return min;
    return min === 0 ? shortSide : Math.min(min, shortSide);
  }, 0);

  const previewLayout = computeSourcePreviewLayout(
    sourceNaturalSize?.width ?? 0,
    sourceNaturalSize?.height ?? 0,
    logoReferenceWidth,
    logoReferenceHeight,
    logoReferenceShortSide,
    outputCropSize,
    aspectRatioHint,
  );

  useEffect(() => {
    setSourceNaturalSize(null);
  }, [sourceImageUrl]);

  const updateLogoNaturalSize = useCallback((assetId: number, naturalWidth: number, naturalHeight: number) => {
    if (!naturalWidth || !naturalHeight) return;
    setLogoNaturalSizes((prev) => {
      const current = prev[assetId];
      if (current?.width === naturalWidth && current?.height === naturalHeight) return prev;
      return { ...prev, [assetId]: { width: naturalWidth, height: naturalHeight } };
    });
  }, []);

  const hasPreviewContent = Boolean(sourceImageUrl) || selectedItems.length > 0;

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-white shadow-inner"
      style={{ aspectRatio: `${previewLayout.aspectRatio}` }}
    >
      <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/10" />

      {sourceImageUrl && (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={sourceImageUrl}
            alt="底图预览"
            draggable={false}
            className="absolute inset-0 h-full w-full select-none object-cover object-bottom"
            onLoad={(event) => {
              const img = event.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setSourceNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
              }
            }}
          />
        </>
      )}

      {/* Overlay logos */}
      {selectedItems.map((item) => {
        const thumb = resolveThumb(item.asset_id);
        if (!thumb) return null;
        const original = isOriginalMode(item);
        const logoNaturalWidth = resolveLogoWidth?.(item.asset_id) || logoNaturalSizes[item.asset_id]?.width || null;
        const logoWidthPercent = logoNaturalWidth
          ? Math.max(4, Math.min(100, (logoNaturalWidth / previewLayout.canvasReferenceWidth) * 100))
          : undefined;
        return (
          <div
            key={`${item.library_id}-${item.asset_id}`}
            style={getLogoOverlayStyle(item, logoWidthPercent, previewLayout.canvasReferenceWidth)}
            className="flex items-center justify-center"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={thumb}
              alt={resolveName(item.asset_id)}
              onLoad={(e) => updateLogoNaturalSize(item.asset_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
              className={
                original
                  ? "h-auto w-full max-w-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.18)]"
                  : "h-auto w-full max-w-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
              }
            />
          </div>
        );
      })}

      {/* Empty state hint */}
      {!hasPreviewContent && (
        <div className="absolute inset-x-4 bottom-4 rounded-lg bg-black/55 px-3 py-2 text-center text-[11px] text-white/80 backdrop-blur-sm">
          选择 Logo 后在此预览叠加效果
        </div>
      )}
      {sourceImageUrl && selectedItems.length === 0 && (
        <div className="absolute inset-x-4 bottom-4 rounded-lg bg-black/55 px-3 py-2 text-center text-[11px] text-white/80 backdrop-blur-sm">
          已加载上传图片，选择 Logo 后预览叠加效果
        </div>
      )}
    </div>
  );
}

function LogoLibraryModal({
  open,
  libraries,
  items,
  selectedItems,
  onSelect,
  onRemove,
  onUpdate,
  onClose,
  onLoadItems,
  resolveThumb,
  resolveName,
  resolveLogoWidth,
  resolveLogoHeight,
  sourceImageUrl,
  outputCropSize,
  aspectRatioHint,
  sourceAspectRatio,
  reportNaturalSize,
  naturalSizes,
}: {
  open: boolean;
  libraries: BrandAssetLogoLibrary[];
  items: Record<number, BrandAssetLogoItem[]>;
  selectedItems: LogoPresetItem[];
  onSelect: (
    library: BrandAssetLogoLibrary,
    item: BrandAssetLogoItem,
  ) => void | Promise<void>;
  onRemove: (assetId: number) => void;
  onUpdate: (assetId: number, patch: Partial<LogoPresetItem>) => void;
  onClose: () => void;
  onLoadItems: (libraryId: number) => void;
  resolveThumb: (assetId: number) => string | null;
  resolveName: (assetId: number) => string;
  resolveLogoWidth: (assetId: number) => number | null;
  resolveLogoHeight: (assetId: number) => number | null;
  sourceImageUrl?: string | null;
  outputCropSize?: string | null;
  aspectRatioHint?: string | null;
  sourceAspectRatio?: number | null;
  reportNaturalSize: (assetId: number, width: number, height: number) => void;
  naturalSizes: Record<number, LogoNaturalSize>;
}) {
  const portal = useEcomPortal();
  const [mounted, setMounted] = useState(false);
  const [activeLibraryId, setActiveLibraryId] = useState<number | null>(
    libraries[0]?.id || null,
  );
  // 正在异步添加的素材 id，用于阻止重复点击 + 显示"添加中"反馈。
  const [pendingAddIds, setPendingAddIds] = useState<Set<number>>(() => new Set<number>());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open && libraries.length > 0 && !activeLibraryId) {
      setActiveLibraryId(libraries[0].id);
    }
  }, [open, libraries, activeLibraryId]);

  useEffect(() => {
    if (activeLibraryId && !items[activeLibraryId]) {
      onLoadItems(activeLibraryId);
    }
  }, [activeLibraryId, items, onLoadItems]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !mounted || typeof document === "undefined") return null;

  const currentItems = activeLibraryId ? items[activeLibraryId] || [] : [];
  const selectedCount = selectedItems.length;
  const reachedLimit = selectedCount >= 5;

  const modal = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="选择 Logo 素材"
    >
      <div
        className="flex w-[min(1120px,calc(100vw-32px))] max-h-[86vh] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#161616] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-medium text-white">选择 Logo 素材</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] ${
                reachedLimit
                  ? "bg-amber-500/15 text-amber-300"
                  : "bg-white/5 text-white/60"
              }`}
            >
              已选 {selectedCount}/5
            </span>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="text-white/40 transition-colors hover:text-white"
            aria-label="关闭"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body: two columns on lg+, stacked on smaller screens */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:overflow-hidden">
          {/* Left: tabs + grid */}
          <div className="flex min-h-0 flex-col">
            {/* Library tabs */}
            <div className="flex gap-2 overflow-x-auto border-b border-white/5 px-6 py-3">
              {libraries.map((lib) => (
                <button
                  key={lib.id}
                  type="button"
                  onClick={() => setActiveLibraryId(lib.id)}
                  className={`shrink-0 rounded-md px-3 py-1.5 text-xs transition-colors ${
                    activeLibraryId === lib.id
                      ? "border border-[#00FAE5]/40 bg-[#00FAE5]/15 text-[#00FAE5]"
                      : "border border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
                  }`}
                >
                  {lib.name}
                </button>
              ))}
              {libraries.length === 0 && (
                <span className="text-xs text-white/40">暂无可用素材库</span>
              )}
            </div>

            {/* Logo grid */}
            <div className="min-h-[280px] flex-1 overflow-y-auto px-6 py-5 lg:max-h-none">
              {currentItems.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-xs text-white/40">
                  暂无 Logo 素材
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {currentItems.map((item) => {
                    const isSelected = selectedItems.some((s) => s.asset_id === item.id);
                    const isAdding = pendingAddIds.has(item.id);
                    const disableAdd = !isSelected && reachedLimit;
                    const cardDisabled = disableAdd || isAdding;
                    const dimensionLabel = formatLogoDimension(item);
                    const fallbackSize = naturalSizes[item.id] || null;
                    const isRecommended =
                      !isSelected &&
                      isLogoRecommendedForSource(item, sourceAspectRatio, fallbackSize);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-busy={isAdding}
                        onClick={async () => {
                          if (isSelected) {
                            onRemove(item.id);
                            return;
                          }
                          if (disableAdd || isAdding) return;
                          const lib = libraries.find((l) => l.id === activeLibraryId);
                          if (!lib) return;
                          setPendingAddIds((prev) => {
                            if (prev.has(item.id)) return prev;
                            const next = new Set(prev);
                            next.add(item.id);
                            return next;
                          });
                          try {
                            await onSelect(lib, item);
                          } catch {
                            // 添加失败保持静默，UI 状态由 finally 复位。
                          } finally {
                            setPendingAddIds((prev) => {
                              if (!prev.has(item.id)) return prev;
                              const next = new Set(prev);
                              next.delete(item.id);
                              return next;
                            });
                          }
                        }}
                        disabled={cardDisabled}
                        className={`group relative rounded-lg border p-2 text-left transition-all ${
                          isSelected
                            ? "border-[#00FAE5] bg-[#00FAE5]/10"
                            : disableAdd
                              ? "border-white/15 bg-white/5 opacity-40"
                              : "border-white/15 bg-white/5 hover:border-[#00FAE5]/50"
                        } ${isAdding ? "opacity-70" : ""}`}
                        title={
                          isSelected
                            ? "点击移除"
                            : isAdding
                              ? `${item.name}（正在添加…）`
                              : isRecommended
                                ? `${item.name}（与底图比例相近）`
                                : item.name
                        }
                      >
                        {isRecommended && !isAdding && (
                          <div
                            className="mb-1 inline-flex w-fit rounded-full bg-emerald-200/90 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900 shadow-sm"
                            aria-label="推荐：与底图比例相近"
                          >
                            推荐
                          </div>
                        )}
                        <LogoGridPreview item={item} onNaturalSize={reportNaturalSize} />
                        <div className="truncate text-[11px] text-white/80">{item.name}</div>
                        {isAdding ? (
                          <div className="mt-0.5 text-[10px] text-[#00FAE5]/85">添加中…</div>
                        ) : dimensionLabel ? (
                          <div className="mt-0.5 text-[10px] text-white/45">{dimensionLabel}</div>
                        ) : null}
                        {isSelected && (
                          <div className="absolute right-1.5 top-1.5 rounded-full bg-[#00FAE5] px-1.5 py-0.5 text-[10px] font-medium text-black shadow">
                            已选
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: preview */}
          <div className="flex min-h-0 flex-col gap-3 border-t border-white/5 bg-[#101010] px-6 py-5 lg:border-l lg:border-t-0">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-white/80">效果预览</div>
              <span className="text-[11px] text-white/40">
                {sourceImageUrl ? "实时预览" : "示意图"}
              </span>
            </div>
            <PreviewCanvas
              selectedItems={selectedItems}
              resolveThumb={resolveThumb}
              resolveName={resolveName}
              resolveLogoWidth={resolveLogoWidth}
              resolveLogoHeight={resolveLogoHeight}
              sourceImageUrl={sourceImageUrl}
              outputCropSize={outputCropSize}
              aspectRatioHint={aspectRatioHint}
            />
            <p className="text-[11px] leading-relaxed text-white/45">
              新选 Logo 默认按<span className="text-white/65"> 原尺寸覆盖 </span>到生成图，可在下方为每个 Logo 切换位置，预览会实时更新。
            </p>
            {selectedItems.length > 0 && (
              <div className="space-y-1.5 overflow-y-auto rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                {selectedItems.map((item) => {
                  const thumb = resolveThumb(item.asset_id);
                  const original = isOriginalMode(item);
                  return (
                    <div
                      key={`sel-${item.library_id}-${item.asset_id}`}
                      className="flex items-center gap-2 text-[11px] text-white/70"
                    >
                      {thumb && (
                        <div
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded"
                          style={{ backgroundColor: LOGO_PREVIEW_BG }}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={thumb} alt="" className="max-h-full max-w-full object-contain" />
                        </div>
                      )}
                      <span className="flex-1 truncate" title={resolveName(item.asset_id)}>
                        {resolveName(item.asset_id)}
                      </span>
                      <span
                        className={`hidden shrink-0 rounded px-1.5 py-0.5 text-[10px] sm:inline-flex ${
                          original
                            ? "bg-emerald-500/15 text-emerald-300"
                            : "bg-white/10 text-white/50"
                        }`}
                        title={original ? "Logo 按原尺寸 1:1 覆盖" : "Logo 按底图宽度比例缩放"}
                      >
                        {original ? "原尺寸" : "比例缩放"}
                      </span>
                      <label className="sr-only" htmlFor={`logo-position-${item.asset_id}`}>
                        Logo 位置
                      </label>
                      <EcomSelect
                        id={`logo-position-${item.asset_id}`}
                        className="h-6 shrink-0 rounded border-none bg-[#252525] px-1 text-[10px] text-white/80 outline-none focus:ring-1 focus:ring-[#00FAE5]/50"
                        value={item.position}
                        onChange={(value) => onUpdate(item.asset_id, { position: value })}
                        aria-label={`${resolveName(item.asset_id)} 的位置`}
                      >
                        {Object.entries(POSITION_LABELS).map(([k, v]) => (
                          <EcomSelectOption key={k} value={k}>
                            {v}
                          </EcomSelectOption>
                        ))}
                      </EcomSelect>
                      <button
                        type="button"
                        onClick={() => onRemove(item.asset_id)}
                        className="shrink-0 text-[10px] text-red-400/80 hover:text-red-300"
                      >
                        移除
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-[#161616] px-6 py-3">
          <span className="text-xs text-white/40">最多选择 5 个 Logo · 点击已选可移除</span>
          <button
            onClick={onClose}
            type="button"
            className="rounded-md bg-[url('/button_bg2.png')] bg-cover bg-center bg-no-repeat px-5 py-1.5 text-[12px] font-medium text-[#00FAE5] transition-[filter] hover:brightness-110"
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, portal.getContainer());
}

export default function LogoPresetPanel({
  moduleCode,
  value,
  onChange,
  sourceImageUrl,
  outputCropSize,
  aspectRatioHint,
}: LogoPresetPanelProps) {
  const feedback = useEcomFeedback();
  const [libraries, setLibraries] = useState<BrandAssetLogoLibrary[]>([]);
  const [items, setItems] = useState<Record<number, BrandAssetLogoItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sourceNaturalAspect, setSourceNaturalAspect] = useState<number | null>(null);
  const [inferredPositions, setInferredPositions] = useState<Record<number, string>>({});
  // 后端 brand_asset_logo_items.width/height 历史数据普遍为 0；此处缓存前端测量到的真实尺寸，
  // 用于"推荐"角标命中和 pickInitialPosition 的方位兜底。
  const [naturalSizes, setNaturalSizes] = useState<Record<number, LogoNaturalSize>>({});
  const naturalSizesRef = useRef<Record<number, LogoNaturalSize>>({});
  useEffect(() => {
    naturalSizesRef.current = naturalSizes;
  }, [naturalSizes]);
  const reportNaturalSize = useCallback((assetId: number, width: number, height: number) => {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
    setNaturalSizes((prev) => {
      const cur = prev[assetId];
      if (cur && cur.width === width && cur.height === height) return prev;
      return { ...prev, [assetId]: { width, height } };
    });
  }, []);
  // 每个 asset 的分析 Promise，前台 / 后台共用，避免重复触发；
  // 即使外层 1.2s 超时，内部 Promise 仍会继续，结果落到 inferredPositions 中。
  const analysisPromisesRef = useRef<Map<number, Promise<string | null>>>(new Map());
  // 追踪父组件最新 value，保证 await 期间多次添加不会用到陈旧快照。
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  // 串行 commit 队列：相邻添加按顺序落到 onChange，避免相互覆盖。
  const commitChainRef = useRef<Promise<unknown>>(Promise.resolve());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const availableLibraries = await brandAssetApi.getAvailableLibraries(moduleCode);
      setLibraries(availableLibraries);
    } catch {
      feedback.error({ code: "E5007", fallback: "operation" });
    } finally {
      setLoading(false);
    }
  }, [moduleCode]);

  useEffect(() => {
    load();
  }, [load]);

  const loadLibraryItems = useCallback((libraryId: number) => {
    if (items[libraryId]) return;
    brandAssetApi.getLogoItems(libraryId)
      .then((list) => setItems((prev) => ({ ...prev, [libraryId]: list })))
      .catch(() => feedback.error({ code: "E5007", fallback: "operation" }));
  }, [items]);

  // 异步读取底图真实宽高，用于推荐角标。失败时静默回退到 outputCropSize / aspectRatioHint。
  useEffect(() => {
    setSourceNaturalAspect(null);
    if (!sourceImageUrl || typeof window === "undefined") return;
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setSourceNaturalAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.onerror = () => {
      // 静默忽略，留给 fallback 处理
    };
    img.src = sourceImageUrl;
    return () => {
      cancelled = true;
    };
  }, [sourceImageUrl]);

  const resolvedSourceAspect = useMemo<number | null>(() => {
    if (sourceNaturalAspect && sourceNaturalAspect > 0) return sourceNaturalAspect;
    const crop = parseOutputCropSize(outputCropSize);
    if (crop && crop.width > 0 && crop.height > 0) return crop.width / crop.height;
    const hint = parseAspectRatioHint(aspectRatioHint);
    if (hint && hint > 0) return hint;
    return null;
  }, [sourceNaturalAspect, outputCropSize, aspectRatioHint]);

  // 启动（或复用）某个素材的像素分析，并把结果同步到 inferredPositions。
  const startAnalysis = useCallback(
    (item: BrandAssetLogoItem): Promise<string | null> | null => {
      const url = item.thumbnail_url || item.file_url;
      if (!url) return null;
      let promise = analysisPromisesRef.current.get(item.id);
      if (!promise) {
        promise = analyzeLogoContentPosition(url).then((pos) => {
          if (pos) {
            setInferredPositions((prev) => {
              if (prev[item.id]) return prev;
              return { ...prev, [item.id]: pos };
            });
          }
          return pos;
        });
        analysisPromisesRef.current.set(item.id, promise);
      }
      return promise;
    },
    [],
  );

  // 已加载的素材后台异步分析透明像素分布，缓存推断方位；跨域 / 不可读时静默失败。
  useEffect(() => {
    const allItems = Object.values(items).flat();
    if (allItems.length === 0) return;
    for (const item of allItems) {
      startAnalysis(item);
    }
  }, [items, startAnalysis]);

  // 点击添加时确保该素材的分析结果可用：缓存命中直接返回，否则限时 await。
  // 失败 / 超时静默返回 undefined，调用方走兜底。
  const ensureInferredPosition = useCallback(
    async (item: BrandAssetLogoItem): Promise<string | undefined> => {
      const cached = inferredPositions[item.id];
      if (cached) return cached;
      const promise = startAnalysis(item);
      if (!promise) return undefined;
      try {
        const pos = await Promise.race<string | null>([
          promise,
          new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), 1200);
          }),
        ]);
        return pos ?? undefined;
      } catch {
        return undefined;
      }
    },
    [inferredPositions, startAnalysis],
  );

  // 异步添加：先尝试获取像素分析结果，再用最新 valueRef 序列化 commit 到父组件。
  // 重复点击同一素材由弹窗内的 pending 集合拦截；不同素材的 commit 通过 commitChainRef 串行。
  const addLogo = (library: BrandAssetLogoLibrary, item: BrandAssetLogoItem): Promise<void> => {
    const next = commitChainRef.current.then(async () => {
      if (valueRef.current.items.some((preset) => preset.asset_id === item.id)) return;
      if (valueRef.current.items.length >= 5) {
        feedback.warning("最多可添加 5 个 Logo");
        return;
      }
      const analyzed = await ensureInferredPosition(item);
      if (valueRef.current.items.some((preset) => preset.asset_id === item.id)) return;
      if (valueRef.current.items.length >= 5) {
        feedback.warning("最多可添加 5 个 Logo");
        return;
      }
      const defaults = parseDefaultConfig(item);
      const initialPosition = pickInitialPosition(
        item,
        defaults,
        analyzed,
        naturalSizesRef.current[item.id] ?? null,
      );
      const nextValue: LogoPresetPayload = {
        enabled: true,
        items: [
          ...valueRef.current.items,
          toPresetItem(
            library,
            item,
            valueRef.current.items.length,
            initialPosition,
            naturalSizesRef.current[item.id] ?? null,
          ),
        ],
      };
      valueRef.current = nextValue;
      onChange(nextValue);
    });
    // 链条永不断裂，避免一次失败影响后续点击。
    commitChainRef.current = next.catch(() => undefined);
    return next;
  };

  const removeLogo = (assetId: number) => {
    onChange({ ...value, items: value.items.filter((item) => item.asset_id !== assetId) });
  };

  const updateLogo = (assetId: number, patch: Partial<LogoPresetItem>) => {
    onChange({
      ...value,
      items: value.items.map((item) => (item.asset_id === assetId ? { ...item, ...patch } : item)),
    });
  };

  const getItemName = (assetId: number): string => {
    for (const list of Object.values(items)) {
      const found = list.find((i) => i.id === assetId);
      if (found) return found.name;
    }
    return `Logo #${assetId}`;
  };

  const getItemThumb = (assetId: number): string | null => {
    for (const list of Object.values(items)) {
      const found = list.find((i) => i.id === assetId);
      if (found) return found.thumbnail_url || found.file_url;
    }
    return null;
  };

  const getItemWidth = (assetId: number): number | null => {
    for (const list of Object.values(items)) {
      const found = list.find((i) => i.id === assetId);
      if (found?.width) return found.width;
    }
    return null;
  };

  const getItemHeight = (assetId: number): number | null => {
    for (const list of Object.values(items)) {
      const found = list.find((i) => i.id === assetId);
      if (found?.height) return found.height;
    }
    return null;
  };

  useEffect(() => {
    if (value.items.length === 0) return;
    const libraryIds = Array.from(new Set(value.items.map((i) => i.library_id)));
    for (const libId of libraryIds) {
      if (!items[libId]) {
        brandAssetApi.getLogoItems(libId)
          .then((list) => setItems((prev) => ({ ...prev, [libId]: list })))
          .catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.items]);

  return (
    <div className="mb-3 rounded-xl border border-white/10 bg-[#1A1A1A] p-3">
      <div className="flex items-center gap-3">
        <div>
          <div className="text-sm font-medium text-white">Logo 预设 <span className="text-red-400 text-xs">*</span></div>
          <div className="mt-0.5 text-xs text-white/45">批量任务会默认叠加所选 Logo</div>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {/* Selected logos display */}
        {value.items.length > 0 && (
          <div className="space-y-1.5">
            {value.items.map((item) => {
              const thumb = getItemThumb(item.asset_id);
              return (
                <div key={`${item.library_id}-${item.asset_id}`} className="flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-1.5">
                  {thumb && (
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded"
                      style={{ backgroundColor: LOGO_PREVIEW_BG }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={thumb} alt="" className="max-h-full max-w-full object-contain" />
                    </div>
                  )}
                  <span className="flex-1 truncate text-xs text-white/70">{getItemName(item.asset_id)}</span>
                  <label className="sr-only" htmlFor={`logo-position-panel-${item.asset_id}`}>
                    {getItemName(item.asset_id)} 的位置
                  </label>
                  <EcomSelect
                    id={`logo-position-panel-${item.asset_id}`}
                    aria-label={`${getItemName(item.asset_id)} 的位置`}
                    className="h-6 rounded bg-[#252525] px-1 text-[10px] text-white/70 border-none outline-none"
                    value={item.position}
                    onChange={(value) => updateLogo(item.asset_id, { position: value })}
                  >
                    {Object.entries(POSITION_LABELS).map(([k, v]) => (
                      <EcomSelectOption key={k} value={k}>{v}</EcomSelectOption>
                    ))}
                  </EcomSelect>
                  <button
                    type="button"
                    className="text-[10px] text-red-400/70 hover:text-red-300"
                    onClick={() => removeLogo(item.asset_id)}
                  >
                    移除
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Open library modal button */}
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={loading || value.items.length >= 5}
          className="w-full h-8 rounded-md border border-dashed border-white/15 bg-white/5 text-xs text-white/60 hover:bg-white/10 hover:border-[#00FAE5]/40 disabled:opacity-40 transition-colors"
        >
          {value.items.length >= 5 ? "已达上限 (5/5)" : "+ 选择 Logo 素材"}
        </button>
      </div>

      <LogoLibraryModal
        open={modalOpen}
        libraries={libraries}
        items={items}
        selectedItems={value.items}
        onSelect={addLogo}
        onRemove={removeLogo}
        onUpdate={updateLogo}
        onClose={() => setModalOpen(false)}
        onLoadItems={loadLibraryItems}
        resolveThumb={getItemThumb}
        resolveName={getItemName}
        resolveLogoWidth={getItemWidth}
        resolveLogoHeight={getItemHeight}
        sourceImageUrl={sourceImageUrl}
        outputCropSize={outputCropSize}
        aspectRatioHint={aspectRatioHint}
        sourceAspectRatio={resolvedSourceAspect}
        reportNaturalSize={reportNaturalSize}
        naturalSizes={naturalSizes}
      />
    </div>
  );
}
